import { defineStore } from "pinia";
import { privateBoaHttp, publicBoaHttp, honoClient } from "@/boa-api-client";
import fixedEncodeURIComponent from "@/util/fixed-encode-uri-component";
import { HTTPError } from "ky";
import type { User } from "best-of-awards-api";

type State = {
  userGroups: Array<{ name: string; description: string }>;
};

export default defineStore("users", {
  state: (): State => ({
    userGroups: [],
  }),
  getters: {},
  actions: {
    async userExists(email: string) {
      try {
        const response = await publicBoaHttp
          .get(`users/check/${fixedEncodeURIComponent(email)}`)
          .json<{ success: boolean; userExists: boolean }>();
        return response.success && response.userExists;
      } catch (error: any) {
        console.error(error.message);
        return false;
      }
    },
    async createUser(newUser: any) {
      try {
        await privateBoaHttp.put("users", { json: newUser }).json();
        return { success: true };
      } catch (error: any) {
        const { message } = await error.response.json();
        return { message };
      }
    },
    async deleteUser(username: string) {
      try {
        await privateBoaHttp.delete(`users/${username}`).json();
        return { success: true };
      } catch (error: any) {
        const { message } = await error.response.json();
        return { message };
      }
    },
    async updateUser(
      user: any
    ): Promise<
      { success: true; user: User } | { success: false; message: string }
    > {
      try {
        const response = await privateBoaHttp
          .post(`users/${user.username}`, { json: { user } })
          .json<{ success: true; user: User }>();
        return response;
      } catch (error: any) {
        const { message } = await error.response.json();
        return { success: false, message };
      }
    },
    async getUsers({ email = "" }) {
      try {
        const response: any = await privateBoaHttp
          .get(`users?email=${fixedEncodeURIComponent(email)}`)
          .json();
        if (response.success) {
          return response;
        }
        return null;
      } catch (error: any) {
        console.error(error.message);
      }
    },
    async getUserGroups() {
      const response = await honoClient.users["user-groups"].$get();
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const groups = await response.json();
      this.userGroups = groups;
    },
    // reset user's password as admin
    async resetUserPassword(
      username: string
    ): Promise<{ success: true } | { success: false; message: string }> {
      try {
        const response = await privateBoaHttp
          .get(`reset-user-password/${fixedEncodeURIComponent(username)}`)
          .json<{ success: true }>();
        return response;
      } catch (error: any) {
        const response = error.response.json();
        return { success: false, message: response.message };
      }
    },
    async requestUserAccount(
      candidateId: string,
      toEmail: string,
      from: {
        email: string;
        firstName: string;
        lastName: string;
        phone: string;
        title: string;
      }
    ): Promise<{ success: boolean; message?: string }> {
      try {
        const response: { success: boolean; message?: string } =
          await publicBoaHttp
            .post("request-user-account", {
              json: {
                candidateId,
                toEmail,
                from,
              },
            })
            .json();
        return response;
      } catch (error) {
        if (error instanceof HTTPError) {
          const response = await error.response.json();
          return response;
        }
        return { success: false, message: "An unknown error occurred." };
      }
    },
    async approveUserAccount(
      requestId: string
    ): Promise<{ success: boolean; message?: string }> {
      try {
        const response: { success: boolean; message?: string } =
          await publicBoaHttp.get(`approve-user-account/${requestId}`).json();
        return response;
      } catch (error) {
        if (error instanceof HTTPError) {
          const response = await error.response.json();
          return response;
        }
        return { success: false, message: "An unknown error occurred." };
      }
    },
    clear() {
      this.$reset();
    },
  },
});
